import type { KataScoringDefinition, ScoringCriterion } from './types'

function c(
  id: string,
  text: string,
  suggested: ScoringCriterion['suggested'],
  hint?: string,
): ScoringCriterion {
  return hint ? { id, text, suggested, hint } : { id, text, suggested }
}

function osaeEscapeCriteria(techniqueKey: string): ScoringCriterion[] {
  return [
    c(
      `katame-${techniqueKey}-escape-attempts`,
      'During the technique Uke must demonstrate three attempts to escape.',
      'big',
    ),
    c(
      `katame-${techniqueKey}-escape-reactions`,
      'For each attempt to escape, Tori shows the respecting reactions which should be connected and logical.',
      'medium',
    ),
  ]
}

const generalCeremony: ScoringCriterion[] = [
  c('katame-gen-start-pos', 'Uke decides the starting position', 'medium'),
  c(
    'katame-gen-movement-bow',
    'Movement: at the start Uke takes a step forward; at the end Tori takes two steps back and then Uke takes one step back. Bowing.',
    'medium',
  ),
  c(
    'katame-gen-osaekomi-distance',
    'Osae-komi-waza distance and position: from kyoshi, toma (~1.2 m) and chikama (~30 cm) transitions between Kesa-gatame through Kuzure-kami-shiho-gatame as specified in the criteria sheet.',
    'small_medium',
  ),
  c(
    'katame-gen-shime-distance',
    'Shime-waza distance and position: Tori moves from toma/chikama in kyoshi, astride Uke for strangles, walks behind Uke for Hadaka-jime sequence through Gyaku-juji-jime as specified.',
    'small_medium',
  ),
  c(
    'katame-gen-kansetsu-distance',
    'Kansetsu-waza distance and position: transitions for Ude-garami through Ashi-garami; conclusion with Tori two steps back to toma, Uke one step, bow as specified.',
    'small_medium',
  ),
  c('katame-gen-shikko', 'Tori or Uke lift their knee while moving (Shikko)', 'small_medium'),
  c(
    'katame-gen-steps-chikama',
    'Number of steps are correct and the distance is not, or Tori does not come back to chikama at the end of every technique',
    'medium',
  ),
  c('katame-gen-half-step', 'Tori approaches half a step before performing a technique', 'small'),
  c(
    'katame-gen-wrong-kyoshi',
    'Tori in kyoshi takes a wrong position to Uke’s body before starting a technique',
    'small_medium',
  ),
  c(
    'katame-gen-chikama-break',
    'Tori in chikama breaks his position going half a step forward or backward',
    'small_medium',
  ),
]

/** Practice judging: 16 scored elements (max 160); opening/closing bows are catalog-only. */
export const katameNoKataScoring: KataScoringDefinition = {
  kataId: 'katame-no-kata',
  maxPerJudge: 160,
  maxPanelOfThree: 480,
  elements: [
    {
      id: 'katame-general',
      kind: 'ceremony',
      name: 'General elements',
      nameJa: '一般観察事項',
      criteria: generalCeremony,
    },
    {
      id: 'katame-kesa-gatame',
      kind: 'technique',
      name: 'Kesa-gatame',
      nameJa: '袈裟固',
      techniqueId: 'katame-kesa-gatame',
      groupId: 'osaekomi-waza',
      criteria: [
        c(
          'katame-kesa-gatame-perf',
          'Way of performing: right arm grasp, control left shoulder under left armpit, and related hold details.',
          'small_medium',
        ),
        ...osaeEscapeCriteria('kesa-gatame'),
      ],
    },
    {
      id: 'katame-kata-gatame',
      kind: 'technique',
      name: 'Kata-gatame',
      nameJa: '肩固',
      techniqueId: 'katame-kata-gatame',
      groupId: 'osaekomi-waza',
      criteria: [
        c(
          'katame-kata-gatame-perf',
          'Way of performing: push Uke’s right elbow towards Uke’s right ear, and related hold details.',
          'small_medium',
        ),
        ...osaeEscapeCriteria('kata-gatame'),
      ],
    },
    {
      id: 'katame-kami-shiho-gatame',
      kind: 'technique',
      name: 'Kami-shiho-gatame',
      nameJa: '上四方固',
      techniqueId: 'katame-kami-shiho-gatame',
      groupId: 'osaekomi-waza',
      criteria: [
        c(
          'katame-kami-shiho-gatame-perf',
          'Way of performing: normal approach plus kneeling approach, hands, and related hold details.',
          'small_medium',
        ),
        ...osaeEscapeCriteria('kami-shiho-gatame'),
      ],
    },
    {
      id: 'katame-yoko-shiho-gatame',
      kind: 'technique',
      name: 'Yoko-shiho-gatame',
      nameJa: '横四方固',
      techniqueId: 'katame-yoko-shiho-gatame',
      groupId: 'osaekomi-waza',
      criteria: [
        c(
          'katame-yoko-shiho-gatame-perf',
          'Way of performing: arm movement, knee against Uke’s armpit, belt grip (left-right hand), blocking with right knee, and related details.',
          'small_medium',
        ),
        ...osaeEscapeCriteria('yoko-shiho-gatame'),
      ],
    },
    {
      id: 'katame-kuzure-kami-shiho-gatame',
      kind: 'technique',
      name: 'Kuzure-kami-shiho-gatame',
      nameJa: '崩上四方固',
      techniqueId: 'katame-kuzure-kami-shiho-gatame',
      groupId: 'osaekomi-waza',
      criteria: [
        c(
          'katame-kuzure-kami-shiho-gatame-perf',
          'Way of performing: normal approach plus one step forward diagonally, arm grip, placing Uke’s right arm on Tori’s right thigh, and related details.',
          'small_medium',
        ),
        ...osaeEscapeCriteria('kuzure-kami-shiho-gatame'),
      ],
    },
    {
      id: 'katame-kata-juji-jime',
      kind: 'technique',
      name: 'Kata-juji-jime',
      nameJa: '片十字絞',
      techniqueId: 'katame-kata-juji-jime',
      groupId: 'shime-waza',
      criteria: [
        c(
          'katame-kata-juji-jime-perf',
          'Way of performing: knees near Uke’s armpits, hands; Tori blocks and controls Uke’s body with his legs.',
          'small_medium',
        ),
        c(
          'katame-kata-juji-jime-escape',
          'Uke tries to escape by pushing Tori’s both arms with both hands. Tori continues with the strangle moving his head forward towards the mat.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-hadaka-jime',
      kind: 'technique',
      name: 'Hadaka-jime',
      nameJa: '裸絞',
      techniqueId: 'katame-hadaka-jime',
      groupId: 'shime-waza',
      criteria: [
        c('katame-hadaka-jime-perf', 'Way of performing: both arms simultaneously, and related strangle details.', 'small_medium'),
        c(
          'katame-hadaka-jime-escape',
          'Uke tries to escape by pulling down with both hands on Tori’s upper arm. Tori continues the choke moving backward and controlling Uke.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-okuri-eri-jime',
      kind: 'technique',
      name: 'Okuri-eri-jime',
      nameJa: '送襟絞',
      techniqueId: 'katame-okuri-eri-jime',
      groupId: 'shime-waza',
      criteria: [
        c('katame-okuri-eri-jime-perf', 'Way of performing: left-right-left hand, and related strangle details.', 'small_medium'),
        c(
          'katame-okuri-eri-jime-escape',
          'Uke tries to escape by pulling down with both hands on Tori’s upper arm. Tori continues the strangle moving backward lightly supporting Uke’s body with his right knee and controlling Uke.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-kataha-jime',
      kind: 'technique',
      name: 'Kataha-jime',
      nameJa: '片羽絞',
      techniqueId: 'katame-kataha-jime',
      groupId: 'shime-waza',
      criteria: [
        c(
          'katame-kataha-jime-perf',
          'Way of performing: hands, left palm and arm; Tori turns to his right and strangles moving his right leg backward and on the side.',
          'small_medium',
        ),
        c(
          'katame-kataha-jime-escape',
          'Uke tries to escape by taking his left wrist with his right hand and pulling downward. Tori continues the strangle.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-gyaku-juji-jime',
      kind: 'technique',
      name: 'Gyaku-juji-jime',
      nameJa: '逆十字絞',
      techniqueId: 'katame-gyaku-juji-jime',
      groupId: 'shime-waza',
      criteria: [
        c(
          'katame-gyaku-juji-jime-perf',
          'Way of performing: knees near Uke’s armpits, hands; Tori blocks and controls Uke’s body with his legs.',
          'small_medium',
        ),
        c(
          'katame-gyaku-juji-jime-escape',
          'Uke tries to escape by pushing with his left hand and pulling down with his right hand. Tori continues the strangle and rolls down onto his side crossing his feet.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-ude-garami',
      kind: 'technique',
      name: 'Ude-garami',
      nameJa: '腕緘',
      techniqueId: 'katame-ude-garami',
      groupId: 'kansetsu-waza',
      criteria: [
        c(
          'katame-ude-garami-perf',
          'Tori moves Uke’s right arm and approaches; Uke attacks trying to grasp Tori’s right collar with his left hand.',
          'small_medium',
        ),
        c(
          'katame-ude-garami-escape',
          'Uke tries to escape by lifting his left shoulder and his back. Tori continues the arm lock.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-ude-hishigi-juji-gatame',
      kind: 'technique',
      name: 'Ude-hishigi-juji-gatame',
      nameJa: '腕挫十字固',
      techniqueId: 'katame-ude-hishigi-juji-gatame',
      groupId: 'kansetsu-waza',
      criteria: [
        c(
          'katame-ude-hishigi-juji-gatame-perf',
          'While Tori is approaching, Uke attempts to reach up to grasp Tori’s left collar with his right hand; Tori blocks the arm against his chest grabbing Uke’s right wrist with his right hand first and then his left below, right foot under Uke’s armpit, steps around Uke’s head with his left foot; catches and holds Uke’s arm with his thighs and tightens his knees, lies down and lifts his hips.',
          'small_medium',
        ),
        c(
          'katame-ude-hishigi-juji-gatame-escape',
          'Uke tries to escape lifting his back and pulling his arm while turning left. Tori continues the arm lock.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-ude-hishigi-ude-gatame',
      kind: 'technique',
      name: 'Ude-hishigi-ude-gatame',
      nameJa: '腕挫腕固',
      techniqueId: 'katame-ude-hishigi-ude-gatame',
      groupId: 'kansetsu-waza',
      criteria: [
        c(
          'katame-ude-hishigi-ude-gatame-perf',
          'While Tori is approaching (after moving Uke’s right arm) Uke attempts to reach up to grasp Tori’s right collar with his left hand; Tori performs an arm lock, locking Uke’s left wrist with right side of his neck and his right shoulder, right-left hand on the elbow, control of Uke’s body with right knee.',
          'small_medium',
        ),
        c(
          'katame-ude-hishigi-ude-gatame-escape',
          'Uke tries to escape pulling his arm downward. Tori continues the arm lock.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-ude-hishigi-hiza-gatame',
      kind: 'technique',
      name: 'Ude-hishigi-hiza-gatame',
      nameJa: '腕挫膝固',
      techniqueId: 'katame-ude-hishigi-hiza-gatame',
      groupId: 'kansetsu-waza',
      criteria: [
        c(
          'katame-ude-hishigi-hiza-gatame-perf',
          'Tori places his left palm on Uke’s right elbow and traps Uke’s wrist under his left armpit, puts his right foot on Uke’s groin area, lies down on his right side, his left foot turned inside on Uke’s right side while controlling Uke with the tip of his foot; Tori pushes with the inside of his left knee on his left hand and performs the arm lock.',
          'small_medium',
        ),
        c(
          'katame-ude-hishigi-hiza-gatame-escape',
          'Uke tries to escape pushing his arm forward. Tori continues the arm lock.',
          'medium',
        ),
      ],
    },
    {
      id: 'katame-ashi-garami',
      kind: 'technique',
      name: 'Ashi-garami',
      nameJa: '足緘',
      techniqueId: 'katame-ashi-garami',
      groupId: 'kansetsu-waza',
      criteria: [
        c(
          'katame-ashi-garami-perf',
          'Tori attempts to perform Tomoe-nage; Uke defends moving forward with his right foot and pulling up Tori’s body. Tori pushes the inside of Uke’s left knee with his right foot and circles Uke’s right knee with his left leg and performs a leg lock.',
          'small_medium',
        ),
        c(
          'katame-ashi-garami-escape',
          'Uke tries to escape turning left. Tori continues the leg lock.',
          'medium',
        ),
      ],
    },
  ],
}
